import { apiErrorMessage, apiErrorStatus } from '@/api/client';
import { documentsApi } from '@/api/documents.api';
import { CheckIcon, PlayIcon, UploadIcon, WarningIcon } from '@/components/common/icons';
import { DROP_ZONE, DROP_ZONE_ACTIVE, SECTION_LABEL } from '@/components/common/surfaces';
import type { DetectionOutcome } from '@/hooks/useUploadFlow';
import type { CatalogCategoryModel } from '@/types/catalog.types';
import type { DocumentModel } from '@/types/documents.types';
import { categoryLabel } from '@/utils/categories';
import { extractDroppedFiles } from '@/utils/dropFiles';
import { formatBytes, validatePdfFile } from '@/utils/validators';
import type { ChangeEvent, DragEvent, FC } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface UploadCardProps {
  /** The uploaded document waiting for its run, or `null` before anything is uploaded. */
  document: DocumentModel | null;
  /** How the last run of `document` ended, or `null` when it has not run yet. */
  outcome: DetectionOutcome | null;
  isStarting: boolean;
  /** The catalogue, for the legend of the categories every run detects. */
  categories: CatalogCategoryModel[];
  /** Called with the document the server opened a session for. */
  onUploaded: (document: DocumentModel) => void;
  onStart: () => void;
}

/** How often a full server is asked again while the user waits for a free place. */
export const QUEUE_RETRY_MS = 12_000;
/** How long the wait may last before the user is told to come back later. */
const QUEUE_MAX_WAIT_MS = 30 * 60 * 1000;

/** Resolve after `ms`, or as soon as `signal` aborts. */
const wait = (ms: number, signal: AbortSignal): Promise<void> =>
  new Promise((resolve) => {
    const timer = setTimeout(resolve, ms);
    signal.addEventListener(
      'abort',
      () => {
        clearTimeout(timer);
        resolve();
      },
      { once: true }
    );
  });

/**
 * Which upload failure to name when the backend sends no `detail` of its own: over the limit, or
 * the server already holds as many documents as it can. Everything else is one sentence.
 */
const uploadErrorKey = (status: number | undefined): string => {
  if (status === 413) return 'documents.upload.errors.tooLarge';
  if (status === 429 || status === 503) return 'documents.upload.errors.tooManyOpen';
  if (status === undefined) return 'documents.upload.errors.serverUnavailable';
  return 'documents.upload.errors.failed';
};

/**
 * The prototype's upload block: the drop zone (empty, uploading, or holding the uploaded file),
 * the "Spustit detekci" row with its hint, and the legend of the categories always detected.
 *
 * The whole zone is one button that opens the file dialog, and also a drop target; a file is
 * checked here (extension, emptiness, 50 MB) before a byte goes over the wire. A PDF without a
 * text layer is a scan, which is out of scope, so the run button stays disabled and says why.
 *
 * When the server holds as many documents as it may (429), the file is not refused: it waits in the
 * browser, the upload is retried every `QUEUE_RETRY_MS` and the zone says the queue is full. The wait
 * can be cancelled and ends by itself after `QUEUE_MAX_WAIT_MS`. It is not a server-side queue, so the
 * order of those waiting is not guaranteed and closing the page drops the request.
 */
export const UploadCard: FC<UploadCardProps> = ({ document, outcome, isStarting, categories, onUploaded, onStart }) => {
  const { t, i18n } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  // Drag events fire again for every child the pointer crosses, so enters and leaves are counted.
  const dragDepth = useRef(0);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadingName, setUploadingName] = useState('');
  const [error, setError] = useState<string | null>(null);
  // Set while the server is full and the upload is being retried; `attempts` counts the tries so far.
  const [queue, setQueue] = useState<{ attempts: number } | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  // Leaving the page ends the wait; otherwise the retries would go on in the background.
  useEffect(() => () => abortRef.current?.abort(), []);

  const upload = useCallback(
    async (file: File) => {
      const problem = validatePdfFile(file);
      if (problem) {
        setError(t(`documents.upload.problems.${problem}`));
        return;
      }
      setError(null);
      setUploadingName(file.name);
      setIsUploading(true);
      const controller = new AbortController();
      abortRef.current = controller;
      const startedAt = Date.now();
      let attempts = 0;
      try {
        for (;;) {
          attempts += 1;
          try {
            const created = await documentsApi.upload(file);
            if (controller.signal.aborted) {
              // Cancelled while this try was in flight: do not leave the session behind.
              await documentsApi.delete(created.id).catch(() => undefined);
            } else {
              onUploaded(created);
            }
            return;
          } catch (err) {
            if (apiErrorStatus(err) !== 429) throw err;
            if (controller.signal.aborted) return;
            if (Date.now() - startedAt >= QUEUE_MAX_WAIT_MS) {
              setError(t('documents.upload.errors.queueTimeout'));
              return;
            }
            setQueue({ attempts });
            await wait(QUEUE_RETRY_MS, controller.signal);
            if (controller.signal.aborted) return;
          }
        }
      } catch (err) {
        setError(apiErrorMessage(err, t(uploadErrorKey(apiErrorStatus(err)))));
      } finally {
        if (abortRef.current === controller) abortRef.current = null;
        setQueue(null);
        setIsUploading(false);
      }
    },
    [onUploaded, t]
  );

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Cleared so choosing the same file again after a refusal still fires `change`.
    event.target.value = '';
    if (file) upload(file);
  };

  const handleDragEnter = (event: DragEvent<HTMLButtonElement>) => {
    event.preventDefault();
    dragDepth.current += 1;
    setIsDragging(true);
  };

  const handleDragLeave = (event: DragEvent<HTMLButtonElement>) => {
    event.preventDefault();
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setIsDragging(false);
  };

  const handleDrop = (event: DragEvent<HTMLButtonElement>) => {
    event.preventDefault();
    dragDepth.current = 0;
    setIsDragging(false);
    if (isUploading) return;
    const { files, hadDirectory } = extractDroppedFiles(event.dataTransfer);
    if (files.length > 1) {
      setError(t('documents.upload.problems.multiple'));
      return;
    }
    if (files.length === 0) {
      setError(t(hadDirectory ? 'documents.upload.problems.directory' : 'documents.upload.problems.nothing'));
      return;
    }
    upload(files[0]);
  };

  const canStart = document !== null && document.has_text_layer && !isUploading;
  const hint = (() => {
    if (!document) return t('documents.card.noFileHint');
    if (!document.has_text_layer) return t('documents.card.startDisabledReason');
    if (outcome?.kind === 'cancelled') return t('documents.card.cancelledNote');
    return t('documents.card.runHint');
  })();

  return (
    <div className="flex min-w-0 flex-col gap-4">
      <button
        type="button"
        aria-label={document ? t('documents.upload.replaceLabel') : undefined}
        disabled={isUploading}
        onClick={() => inputRef.current?.click()}
        onDragEnter={handleDragEnter}
        onDragOver={(event) => event.preventDefault()}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`${document || isDragging || isUploading ? DROP_ZONE_ACTIVE : DROP_ZONE} flex min-h-[186px] w-full cursor-pointer items-center justify-center p-[26px] font-[inherit] text-ink disabled:cursor-progress`}
      >
        {queue ? (
          <span className="flex w-full flex-col items-center gap-3 text-center" role="status">
            <UploadIcon size={38} strokeWidth={1.6} stroke="#2eb6e0" />
            <span className="text-[15px] font-semibold text-primary">{t('documents.upload.queued')}</span>
            <span className="text-[12.5px] leading-[1.55] text-subtle">
              {t('documents.upload.queuedHint', {
                seconds: QUEUE_RETRY_MS / 1000,
              })}
            </span>
            <span className="text-[12px] text-faint">
              {t('documents.upload.queuedAttempt', {
                name: uploadingName,
                count: queue.attempts,
              })}
            </span>
            <span className="block h-1 w-full max-w-[320px] overflow-hidden bg-hairline" aria-hidden="true">
              <span className="indeterminate-progress-bar block h-full bg-accent" />
            </span>
          </span>
        ) : isUploading ? (
          <span className="flex w-full flex-col items-center gap-3 text-center" role="status">
            <UploadIcon size={38} strokeWidth={1.6} stroke="#2eb6e0" />
            <span className="text-[15px] font-semibold text-primary">
              {t('documents.upload.uploading', { name: uploadingName })}
            </span>
            <span className="text-[12.5px] leading-[1.55] text-subtle">{t('documents.upload.uploadingHint')}</span>
            <span className="block h-1 w-full max-w-[320px] overflow-hidden bg-hairline" aria-hidden="true">
              <span className="indeterminate-progress-bar block h-full bg-accent" />
            </span>
          </span>
        ) : document ? (
          <span className="flex w-full items-center gap-3.5 text-left">
            <span className="flex h-[50px] w-10 flex-none items-end justify-center bg-primary pb-[5px] text-[9.5px] font-bold tracking-[.06em] text-white">
              PDF
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[14.5px] font-semibold" title={document.filename}>
                {document.filename}
              </span>
              <span className="mt-[3px] block text-xs text-subtle">
                {t('documents.card.pages', { count: document.page_count })} ·{' '}
                {formatBytes(document.size_bytes, i18n.language)} ·{' '}
                {document.has_text_layer ? t('documents.card.textLayerOk') : t('documents.card.textLayerMissing')}
              </span>
            </span>
            {document.has_text_layer ? (
              <span className="flex flex-none items-center gap-1.5 bg-ok-soft px-[9px] py-[5px] text-[11.5px] font-semibold text-ok-ink">
                <CheckIcon size={12} strokeWidth={3} />
                {t('documents.upload.ready')}
              </span>
            ) : (
              <span className="flex flex-none items-center gap-1.5 bg-warn-soft px-[9px] py-[5px] text-[11.5px] font-semibold text-warn">
                <WarningIcon size={12} strokeWidth={2.4} />
                {t('documents.upload.noTextLayer')}
              </span>
            )}
          </span>
        ) : (
          <span className="flex flex-col items-center gap-3 text-center">
            <UploadIcon size={38} strokeWidth={1.6} stroke="#2eb6e0" />
            <span className="text-[15px] font-semibold text-primary">
              {isDragging ? t('documents.upload.dropActive') : t('documents.upload.dropTitle')}
            </span>
            <span className="text-[12.5px] leading-[1.55] text-subtle">
              {t('documents.upload.dropHint')}
              <br />
              {t('documents.upload.dropLimits')}
            </span>
          </span>
        )}
      </button>
      <input ref={inputRef} type="file" accept="application/pdf" className="hidden" onChange={handleChange} />
      {queue ? (
        <button
          type="button"
          onClick={() => abortRef.current?.abort()}
          className="cursor-pointer self-start rounded border border-control bg-white px-3.5 py-2 text-xs font-medium text-muted hover:border-primary hover:text-primary"
        >
          {t('documents.upload.cancelWaiting')}
        </button>
      ) : null}

      <div className="flex flex-wrap items-center gap-3.5">
        <button
          type="button"
          disabled={!canStart || isStarting}
          onClick={onStart}
          className={`flex items-center gap-[9px] rounded border-none px-5 py-[13px] text-sm font-semibold ${
            canStart
              ? 'cursor-pointer bg-primary text-white hover:bg-primary-deep'
              : 'cursor-not-allowed bg-control text-faint'
          } disabled:cursor-not-allowed`}
        >
          <PlayIcon size={16} />
          {outcome ? t('documents.card.retry') : t('documents.card.start')}
        </button>
        <span className="text-xs leading-[1.5] text-subtle">{hint}</span>
      </div>

      {error ? (
        <p role="alert" className="m-0 text-xs leading-[1.5] text-danger">
          {error}
        </p>
      ) : null}
      {outcome?.kind === 'failed' ? (
        <p role="alert" className="m-0 text-xs leading-[1.5] text-danger">
          {outcome.message}
        </p>
      ) : null}

      {categories.length > 0 ? (
        <div className="flex flex-wrap gap-x-[22px] gap-y-2 border-t border-line pt-[18px]">
          <span className={`${SECTION_LABEL} w-full`}>{t('documents.card.alwaysDetected')}</span>
          {categories.map((entry) => (
            <span key={entry.key} className="flex items-center gap-[7px] text-xs text-muted">
              <span className="inline-block h-[9px] w-[9px] flex-none" style={{ background: entry.color }} />
              {categoryLabel(entry.key, entry.label_cs)}
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
};
